/**
 * BorrarReservaDialog — Confirmación de borrado hard de una reserva.
 *
 * Pide motivo obligatorio (≥5 chars) y llama al DELETE del backend.
 * El backend re-valida el cutoff aunque el frontend ya lo hizo (defensa
 * en profundidad — un cliente desincronizado no debe lograr borrar tarde).
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Alert } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { StyledDialog, inputStyles } from '../../common';
import { API_BASE, getEmpresaId } from './format';

export default function BorrarReservaDialog({
  reserva,          // { id, codigo_reserva, nombre_cliente, total_pasajeros, ... }
  open,
  onClose,
  onDeleted,        // callback cuando el borrado terminó OK — se pasa a refetch
  userEmail,        // opcional: para auditoría
  userName,         // opcional: para auditoría
}) {
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setMotivo('');
      setError('');
      setLoading(false);
    }
  }, [open, reserva?.id]);

  const handleConfirm = async () => {
    const motivoLimpio = motivo.trim();
    if (motivoLimpio.length < 5) {
      setError('El motivo es obligatorio (mínimo 5 caracteres).');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE}/turismo/reservaciones/${reserva.id}?empresa_id=${getEmpresaId()}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            motivo: motivoLimpio,
            usuario_email: userEmail || undefined,
            usuario_nombre: userName || undefined,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.detail || `Error ${res.status} al borrar la reserva.`);
        setLoading(false);
        return;
      }
      // Éxito — cierra y notifica arriba
      onDeleted?.(data);
      onClose?.();
    } catch (e) {
      setError(`Error de red: ${e.message}`);
      setLoading(false);
    }
  };

  if (!reserva) return null;

  return (
    <StyledDialog
      open={open}
      onClose={loading ? undefined : onClose}
      title="Borrar reserva"
      icon={<DeleteOutlineIcon />}
      variant="compact"
      maxWidth="xs"
    >
      <StyledDialog.Body compact>
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: '#64748b', mb: 0.25 }}>
            Reserva
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', fontFamily: 'monospace' }}>
            {reserva.codigo_reserva}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#475569' }}>
            {reserva.nombre_cliente || 'Sin titular'} · {reserva.total_pasajeros || 0} pax
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12 }}>
          El borrado es <strong>irreversible</strong>. Se eliminan viajeros,
          vouchers, pagos y se liberan los cupos. Queda registro en auditoría.
        </Alert>

        <TextField
          sx={inputStyles}
          label="Motivo del borrado *"
          placeholder="Ej: cliente pidió cancelar por cambio de fecha"
          value={motivo}
          onChange={e => setMotivo(e.target.value)}
          multiline
          minRows={3}
          disabled={loading}
          error={!!error}
          helperText={error || 'Mínimo 5 caracteres. Queda en auditoría.'}
        />
      </StyledDialog.Body>

      <StyledDialog.CompactActions>
        <StyledDialog.CancelButton fullWidth onClick={onClose} disabled={loading}>
          Cancelar
        </StyledDialog.CancelButton>
        <StyledDialog.PrimaryButton
          fullWidth
          onClick={handleConfirm}
          loading={loading}
          sx={{ background: '#dc2626', '&:hover': { background: '#991b1b' } }}
        >
          Borrar
        </StyledDialog.PrimaryButton>
      </StyledDialog.CompactActions>
    </StyledDialog>
  );
}
